import React, { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Stack, Typography, Grid, Button, Card, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// material icons
import UpdateIcon from '@mui/icons-material/Update';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BadgeIcon from '@mui/icons-material/Badge';
import MapIcon from '@mui/icons-material/Map';
import PlaceIcon from '@mui/icons-material/Place';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HomeIcon from '@mui/icons-material/Home';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PublicIcon from '@mui/icons-material/Public';

// components
import { useDispatch } from 'react-redux';
import { normalizeValue } from '../../utils/CommonFunction';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getCustomerView } from '../../store/Slice/Customer/CustomerSlice';
import useCustomerSelector from '../../store/Slice/Customer/CustomerSelector';
import { NOT_AVAILABLE } from '../Global/Constants';
import { fDate } from '../../utils/formatTime';

const getIconBoxStyle = (label) => {
  switch (label) {
    case 'Customer ID':
    case 'Customer Name':
    case 'First Name':
    case 'Middle Name':
    case 'Last Name':
      return { bgcolor: 'rgba(2, 132, 199, 0.08)', color: '#0284c7' };
    case 'Mobile Number':
    case 'Email Address':
      return { bgcolor: 'rgba(16, 185, 129, 0.08)', color: '#10b981' };
    case 'Loan Type':
      return { bgcolor: 'rgba(245, 158, 11, 0.08)', color: '#f59e0b' };
    case 'Address':
    case 'City':
    case 'District':
    case 'Province / State':
    case 'Country':
      return { bgcolor: 'rgba(99, 102, 241, 0.08)', color: '#6366f1' };
    case 'Created On':
    case 'Last Updated On':
      return { bgcolor: 'rgba(14, 165, 233, 0.08)', color: '#0ea5e9' };
    case 'Status':
    case 'Active Account':
      return { bgcolor: 'rgba(34, 197, 94, 0.08)', color: '#22c55e' };
    default:
      return { bgcolor: 'rgba(2, 132, 199, 0.08)', color: '#0284c7' };
  }
};

function CustomerView() {
  const navigate = useNavigate();
  const { CustomerViewData } = useCustomerSelector();
  const location = useLocation();
  const rowId = location.state || null;

  const { LoginData } = useLoginSelector();
  const dispatch = useDispatch();

  const getCustomerViewApi = useCallback(() => {
    if (!rowId) return;
    dispatch(
      getCustomerView({
        customerId: rowId,
        userId: LoginData?.userId
      })
    );
  }, [LoginData?.userId, rowId, dispatch]);

  useEffect(() => {
    getCustomerViewApi();
  }, [getCustomerViewApi]);

  const infoFields = useMemo(() => {
    if (!CustomerViewData) return [];
    return [
      {
        label: 'Customer ID',
        icon: FingerprintIcon,
        value: normalizeValue(CustomerViewData?.customerId, NOT_AVAILABLE)
      },
      {
        label: 'Customer Name',
        icon: BadgeIcon,
        value: normalizeValue(CustomerViewData?.customerName, NOT_AVAILABLE)
      },
      {
        label: 'First Name',
        icon: PersonOutlineIcon,
        value: normalizeValue(CustomerViewData?.customerFirstName, NOT_AVAILABLE)
      },
      {
        label: 'Middle Name',
        icon: PersonOutlineIcon,
        value: normalizeValue(CustomerViewData?.customerMiddleName, NOT_AVAILABLE)
      },
      {
        label: 'Last Name',
        icon: PersonOutlineIcon,
        value: normalizeValue(CustomerViewData?.customerLastName, NOT_AVAILABLE)
      },
      {
        label: 'Mobile Number',
        icon: PhoneIcon,
        value: normalizeValue(CustomerViewData?.mobile, NOT_AVAILABLE)
      },
      {
        label: 'Email Address',
        icon: EmailIcon,
        value: normalizeValue(CustomerViewData?.email, NOT_AVAILABLE)
      },
      {
        label: 'Loan Type',
        icon: RequestQuoteIcon,
        value: normalizeValue(CustomerViewData?.loanType, NOT_AVAILABLE)
      },
      {
        label: 'Address',
        icon: HomeIcon,
        value: normalizeValue(CustomerViewData?.address, NOT_AVAILABLE)
      },
      {
        label: 'City',
        icon: LocationCityIcon,
        value: normalizeValue(CustomerViewData?.cityName, NOT_AVAILABLE)
      },
      {
        label: 'District',
        icon: MapIcon,
        value: normalizeValue(CustomerViewData?.districtId, NOT_AVAILABLE)
      },
      {
        label: 'Province / State',
        icon: PlaceIcon,
        value: normalizeValue(CustomerViewData?.provinceId, NOT_AVAILABLE)
      },
      {
        label: 'Country',
        icon: PublicIcon,
        value: normalizeValue(CustomerViewData?.countryId, NOT_AVAILABLE)
      },
      {
        label: 'Created On',
        icon: CalendarMonthIcon,
        value: CustomerViewData?.createdOn ? fDate(CustomerViewData.createdOn) : NOT_AVAILABLE
      },
      {
        label: 'Last Updated On',
        icon: UpdateIcon,
        value: CustomerViewData?.updatedOn ? fDate(CustomerViewData.updatedOn) : NOT_AVAILABLE
      },
      {
        label: 'Status',
        icon: InfoIcon,
        value: normalizeValue(CustomerViewData?.status, NOT_AVAILABLE)
      },
      {
        label: 'Active Account',
        icon: CheckCircleIcon,
        value:
          CustomerViewData?.isActive !== undefined
            ? CustomerViewData.isActive
              ? 'Yes'
              : 'No'
            : NOT_AVAILABLE
      }
    ];
  }, [CustomerViewData]);

  const currentStatus = CustomerViewData?.status || NOT_AVAILABLE;

  return (
    <Page title="Partner Management System || Customer Details">
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8, pt: 2.5 }}>
        <Container maxWidth="xl">
          {/* Header Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 3.5 }}
          >
            {/* Left: Back Button + Title + Subtitle */}
            <Stack spacing={0.5}>
              <Button
                onClick={() => navigate('/customer')}
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                sx={{
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: '13px',
                  p: 0,
                  minWidth: 0,
                  mb: 0.5,
                  textTransform: 'none',
                  '&:hover': { color: '#0f172a', bgcolor: 'transparent' }
                }}
              >
                Back to Customer List
              </Button>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: '#0f172a',
                  letterSpacing: '-0.5px'
                }}
              >
                Customer Details
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.25 }}>
                View complete customer profile, contact details, and account status.
              </Typography>
            </Stack>

            {/* Right: Breadcrumbs & Current Status */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <BrudCrumbs />
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                  Current Status:
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#fef3c7',
                    color: '#b45309',
                    px: 1.75,
                    py: 0.4,
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    border: '1px solid #fde68a'
                  }}
                >
                  {currentStatus}
                </Box>
              </Stack>
            </Stack>
          </Stack>

          {/* Customer Details Card */}
          <Card
            sx={{
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
              p: { xs: 2.5, md: 3 },
              bgcolor: '#ffffff'
            }}
          >
            {/* Card Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '8px',
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PersonOutlineIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                  Customer Profile &amp; Information
                </Typography>
              </Stack>
            </Stack>

            {/* Field Grid */}
            <Grid container spacing={3}>
              {infoFields.map((item, index) => {
                const iconStyle = getIconBoxStyle(item.label);
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Stack direction="row" spacing={1.75} alignItems="center">
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '12px',
                          bgcolor: iconStyle.bgcolor,
                          color: iconStyle.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <item.icon sx={{ fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}>
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#0f172a',
                            mt: 0.2,
                            wordBreak: 'break-word'
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          </Card>
        </Container>
      </Box>
    </Page>
  );
}

export default CustomerView;
